import { useAuth } from './AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  FileQuestion, 
  BarChart3, 
  LogOut,
  GraduationCap,
  Menu,
  X,
  UserCircle
} from 'lucide-react';
import { useState } from 'react';
import Footer from './Footer';

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (!user) return [];

    const baseItems = [
      { path: `/${user.role}`, icon: LayoutDashboard, label: 'Dashboard' },
    ];

    if (user.role === 'admin') {
      return [
        ...baseItems,
        { path: '/admin/users', icon: Users, label: 'Users' },
        { path: '/admin/courses', icon: BookOpen, label: 'Courses' },
        { path: '/admin/quizzes', icon: FileQuestion, label: 'Quizzes' },
        { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
      ];
    }

    if (user.role === 'teacher') {
      return [
        ...baseItems,
        { path: '/teacher/courses', icon: BookOpen, label: 'My Courses' },
        { path: '/teacher/quizzes', icon: FileQuestion, label: 'Quizzes' },
        { path: '/teacher/students', icon: Users, label: 'Students' },
        { path: '/teacher/analytics', icon: BarChart3, label: 'Analytics' },
      ];
    }

    if (user.role === 'student') {
      return [
        ...baseItems,
        { path: '/student/quizzes', icon: FileQuestion, label: 'My Quizzes' },
        { path: '/student/analytics', icon: BarChart3, label: 'My Analytics' },
      ];
    }

    return baseItems;
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-md"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-600 rounded-lg">
                <GraduationCap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">AP Course Improvement</h1>
                <p className="text-xs text-gray-500 capitalize">{user?.role} Portal</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/profile')}
              className="text-right hidden sm:flex items-center gap-2 hover:bg-gray-100 p-2 rounded-lg transition-colors border border-transparent hover:border-gray-200 cursor-pointer group"
            >
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <UserCircle className="w-8 h-8 text-gray-400 group-hover:text-blue-600" />
            </button>
            <Button onClick={() => navigate('/profile')} variant="ghost" size="sm" className="sm:hidden">
              <UserCircle className="w-5 h-5" />
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-[57px] left-0 h-[calc(100vh-57px)] bg-white border-r border-gray-200 
          w-64 transition-transform duration-200 z-30
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-blue-50 text-blue-700' 
                      : 'text-gray-700 hover:bg-gray-100'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 flex flex-col">
          <div className="flex-1 p-6 lg:p-8">
            {children}
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
}